import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { apiEndpoints } from '../services/api';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { createApiUrlWithPrefix, createApiUrl, getUserImageUrl } from '../config/api';
import PersonnelPaymentManagement from './PersonnelPaymentManagement';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  RefreshCw,
  Users,
  Eye,
  X,
  CreditCard as PaymentIcon
} from 'lucide-react';

const PersonnelPayments = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' or 'payments'
  const [personnel, setPersonnel] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [, setTotalItems] = useState(0);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedPersonnel, setSelectedPersonnel] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [historyPagination, setHistoryPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });
  const [editPaymentData, setEditPaymentData] = useState({
    description: '',
    amount: '',
    type: 'Salary',
    paymentDate: ''
  });
    const [refreshKey, setRefreshKey] = useState(0);
const [paymentData, setPaymentData] = useState({
    description: '',
    amount: '',
    type: 'Salary',
    paymentDate: new Date().toISOString().split('T')[0]
  });

  const paymentTypes = [
    { value: 'Salary', label: t('common.salary') },
    { value: 'Salary Advance', label: t('common.salaryAdvance') },
    { value: 'Bonus', label: t('common.bonus') },
    { value: 'Overtime', label: t('common.overtime') },
    { value: 'Allowance', label: t('common.allowance') },
    { value: 'Reimbursement', label: t('common.reimbursement') },
    { value: 'Other', label: t('common.other') }
  ];

  const fetchPersonnel = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch all personnel (coaches, admins) - SuperAdmin excluded
      const [coachesResponse, adminsResponse] = await Promise.all([
        apiEndpoints.users.getByRole("Coach", { headers: { Authorization: `Bearer ${token}` } }),
        apiEndpoints.users.getByRole("Admin", { headers: { Authorization: `Bearer ${token}` } })
      ]);

      const allPersonnel = [
        ...(coachesResponse.data?.users || []).map(user => ({ ...user, role: 'Coach' })),
        ...(adminsResponse.data?.users || []).map(user => ({ ...user, role: 'Admin' }))
        // Exclude SuperAdmin users from personnel list
      ];

            // Fetch payment summaries for each person
            const personnelWithPayments = await Promise.all(
                allPersonnel.map(async (person) => {
                    try {
                        const paymentsResponse = await axios.get(createApiUrlWithPrefix(`PersonnelPayment/personnel/${person.id}`), {
                            headers: { Authorization: `Bearer ${token}` }
                        });
                        
                        const payments = paymentsResponse.data || [];
                        const totalAmount = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
                        const completedAmount = payments
                            .filter(p => p.status === 'Completed')
                            .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
                        const pendingAmount = payments
                            .filter(p => p.status === 'Pending')
                            .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

                        return {
                            ...person,
                            payments,
                            totalAmount,
                            completedAmount,
                            pendingAmount,
                            totalPayments: payments.length,
                            pendingPayments: payments.filter(p => p.status === 'Pending').length
                        };
                    } catch (error) {
                        return {
                            ...person,
                            payments: [],
                            totalAmount: 0,
                            completedAmount: 0,
                            pendingAmount: 0,
                            totalPayments: 0,
                            pendingPayments: 0
                        };
                    }
                })
            );

      setPersonnel(personnelWithPayments);
    } catch (error) {
      console.error('Error fetching personnel:', error);
      toast.error(t('common.failedToLoadPersonnel'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchPersonnel();
  }, [fetchPersonnel]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      const paymentPayload = {
        personnelId: selectedPersonnel.id,
        paymentType: paymentData.type,
        description: paymentData.description,
        amount: parseFloat(paymentData.amount),
        paymentDate: new Date(paymentData.paymentDate).toISOString(),
        notes: '',
        paymentMethod: 'Bank Transfer',
        referenceNumber: ''
      };

      await axios.post(createApiUrlWithPrefix('PersonnelPayment'), paymentPayload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      toast.success(t('common.paymentCreatedSuccessfully'));
      setShowPaymentModal(false);
      setSelectedPersonnel(null);
      setPaymentData({
        description: '',
        amount: '',
        type: 'Salary',
        paymentDate: new Date().toISOString().split('T')[0]
      });
      fetchPersonnel();
    } catch (error) {
      console.error('Error saving payment:', error);
      toast.error(t('common.failedToSavePayment'));
    }
  };

  const handleAddPayment = (person) => {
    setSelectedPersonnel(person);
    setPaymentData({
      description: `Salary for ${person.firstName} ${person.lastName}`,
      amount: '',
      type: 'Salary',
      paymentDate: new Date().toISOString().split('T')[0]
    });
    setShowPaymentModal(true);
  };

  const handleViewHistory = async (person, page = 1) => {
    setSelectedPersonnel(person);
    setShowHistoryModal(true);
    setHistoryLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(createApiUrlWithPrefix(`PersonnelPayment/personnel/${person.id}?page=${page}&limit=10`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = response.data;
      if (data.payments) {
        // Paginated response
        setPaymentHistory(data.payments);
        setHistoryPagination({
          currentPage: data.currentPage || 1,
          totalPages: data.totalPages || 1,
          totalItems: data.totalItems || 0,
          itemsPerPage: data.itemsPerPage || 10
        });
      } else {
        // Non-paginated response (fallback)
        const payments = data || [];
        setPaymentHistory(payments);
        setHistoryPagination({
          currentPage: 1,
          totalPages: 1,
          totalItems: payments.length,
          itemsPerPage: 10
        });
      }
    } catch (error) {
      console.error('Error fetching payment history:', error);
      toast.error(t('common.failedToLoadPayments'));
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleEditPayment = (payment) => {
    setEditingPayment(payment);
    setEditPaymentData({
      description: payment.description,
      amount: payment.amount.toString(),
      type: payment.paymentType,
      paymentDate: new Date(payment.paymentDate).toISOString().split('T')[0]
    });
  };

  const handleUpdatePayment = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const updatePayload = {
        personnelId: selectedPersonnel.id,
        paymentType: editPaymentData.type,
        description: editPaymentData.description,
        amount: parseFloat(editPaymentData.amount),
        paymentDate: new Date(editPaymentData.paymentDate).toISOString(),
        notes: '',
        paymentMethod: 'Bank Transfer',
        referenceNumber: ''
      };

      await axios.put(createApiUrlWithPrefix(`PersonnelPayment/${editingPayment.id}`), updatePayload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      toast.success(t('common.paymentUpdatedSuccessfully'));
      setEditingPayment(null);
      // Refresh the payment history
      handleViewHistory(selectedPersonnel);
    } catch (error) {
      console.error('Error updating payment:', error);
      toast.error(t('common.failedToSavePayment'));
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm(t('common.deletePaymentConfirm'))) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(createApiUrlWithPrefix(`PersonnelPayment/${paymentId}`), {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(t('common.paymentDeletedSuccessfully'));
      // Refresh the payment history
      handleViewHistory(selectedPersonnel);
    } catch (error) {
      console.error('Error deleting payment:', error);
      toast.error(t('common.failedToDeletePayment'));
    }
  };

  const handleCloseModal = () => {
    setShowPaymentModal(false);
    setShowHistoryModal(false);
    setSelectedPersonnel(null);
    setEditingPayment(null);
    setPaymentData({
      description: '',
      amount: '',
      type: 'Salary',
      paymentDate: new Date().toISOString().split('T')[0]
    });
  };

  const filteredPersonnel = personnel.filter(person =>
    person.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    person.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    person.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    person.role?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination calculations
  const totalPages = Math.ceil(filteredPersonnel.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedPersonnel = filteredPersonnel.slice(startIndex, endIndex);

  // Update total items when personnel or search changes
  useEffect(() => {
    setTotalItems(filteredPersonnel.length);
    setCurrentPage(1); // Reset to first page when search changes
  }, [filteredPersonnel.length]);

  // const getRoleColor = (role) => {
  //   switch (role) {
  //     case 'Coach': return 'bg-blue-100 text-blue-800';
  //     case 'Personnel': return 'bg-green-100 text-green-800';
  //     default: return 'bg-gray-100 text-gray-800';
  //   }
  // };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'Coach': return '🏇';
      case 'Personnel': return '👥';
      default: return '👤';
    }
  };

  // const totalPersonnelAmount = personnel.reduce((sum, person) => sum + person.totalAmount, 0);
  // const totalCompletedAmount = personnel.reduce((sum, person) => sum + person.completedAmount, 0);
  // const totalPendingAmount = personnel.reduce((sum, person) => sum + person.pendingAmount, 0);

  return (
    <>
      <div className="flex flex-col min-h-full bg-neutral-50 px-4 sm:px-6 lg:px-8 py-6" key={refreshKey}>
        {/* Fixed Header Section */}
        <div className="">
          {/* Header - Responsive */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('institutional.personnelPayments.title')}</h1>
              <p className="mt-2 sm:mt-3 text-sm sm:text-base text-gray-600">{t('institutional.personnelPayments.subtitle')}</p>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mt-4">
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 px-6">
                <button
                  onClick={() => setActiveTab('overview')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center transition-colors ${
                    activeTab === 'overview'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Users className="w-4 h-4 mr-2" />
                  {t('common.overview')}
                </button>
                <button
                  onClick={() => setActiveTab('payments')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center transition-colors ${
                    activeTab === 'payments'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <PaymentIcon className="w-4 h-4 mr-2" />
                  {t('institutional.personnelPayments.title')}
                </button>
              </nav>
            </div>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'payments' ? (
          <PersonnelPaymentManagement />
        ) : (
          <>
            {/* Scrollable Content Section */}
            <div className="mt-6">


      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder={t('institutional.personnelPayments.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <div className="flex gap-4">
            <button
              onClick={() => setSearchTerm('')}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t('common.clear')}
            </button>
            
            <button
              onClick={fetchPersonnel}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Personnel Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden animate-pulse">
              <div className="h-32 bg-gray-200"></div>
              <div className="p-6">
                <div className="h-4 bg-gray-200 rounded mb-2"></div>
                <div className="h-4 bg-gray-200 rounded mb-4"></div>
                <div className="h-8 bg-gray-200 rounded"></div>
              </div>
            </div>
          ))
        ) : paginatedPersonnel.length === 0 ? (
          <div className="col-span-full bg-white rounded-2xl shadow-lg border border-gray-100 p-8 text-center">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('common.noPersonnelFound')}</h3>
            <p className="text-gray-500">
{t('common.tryAdjustingSearch')}
            </p>
          </div>
        ) : (
          paginatedPersonnel.map((person) => (
            <div key={person.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg hover:scale-[1.02] transition-all duration-300 group">
              {/* Header with subtle gradient */}
              <div className={`h-2 bg-gradient-to-r ${
                person.role === 'Coach' 
                  ? 'from-blue-500 to-blue-600' 
                  : 'from-green-500 to-green-600'
              }`}></div>
              
              <div className="p-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg ${
                    person.role === 'Coach' 
                      ? 'bg-gradient-to-br from-blue-100 to-blue-200' 
                      : 'bg-gradient-to-br from-green-100 to-green-200'
                  }`}>
                    {getUserImageUrl(person) ? (
                      <img 
                        src={getUserImageUrl(person)} 
                        alt={`${person.firstName} ${person.lastName}`}
                        className="w-full h-full rounded-2xl object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : (
                      <span className={`text-2xl ${
                        person.role === 'Coach' ? 'text-blue-600' : 'text-green-600'
                      }`}>
                        {getRoleIcon(person.role)}
                      </span>
                    )}
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors">
                      {person.firstName} {person.lastName}
                    </h3>
                    <p className="text-sm text-gray-600 mb-2">{person.email}</p>
                    {person.phoneNumber && (
                      <div className="flex items-center text-sm text-gray-500">
                        <span className="w-4 h-4 mr-2">📞</span>
                        <span>{person.phoneNumber}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Role badge */}
                  <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    person.role === 'Coach' 
                      ? 'bg-blue-100 text-blue-800' 
                      : 'bg-green-100 text-green-800'
                  }`}>
{person.role === 'Coach' ? t('common.coach') : person.role === 'Admin' ? t('common.admin') : person.role}
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={() => handleAddPayment(person)}
                    className="flex-1 flex items-center justify-center px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-sm font-semibold rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t('common.addPayment')}
                  </button>
                  <button
                    onClick={() => handleViewHistory(person)}
                    className="flex-1 flex items-center justify-center px-4 py-3 bg-gray-100 text-gray-700 text-sm font-semibold rounded-xl hover:bg-gray-200 transition-all duration-200 shadow-md hover:shadow-lg"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    {t('common.history')}
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>


      {/* Payment Modal */}
      {showPaymentModal && selectedPersonnel && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t('common.addPayment')} for {selectedPersonnel.firstName} {selectedPersonnel.lastName}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('common.paymentTypeLabel')}</label>
                  <select
                    value={paymentData.type}
                    onChange={(e) => setPaymentData({...paymentData, type: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    {paymentTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('common.descriptionLabel')}</label>
                  <input
                    type="text"
                    value={paymentData.description}
                    onChange={(e) => setPaymentData({...paymentData, description: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder={t('common.descriptionPlaceholder')}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('common.amountLabel')}</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={paymentData.amount}
                    onChange={(e) => setPaymentData({...paymentData, amount: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder={t('common.amountPlaceholder')}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('common.paymentDateLabel')}</label>
                  <input
                    type="date"
                    value={paymentData.paymentDate}
                    onChange={(e) => setPaymentData({...paymentData, paymentDate: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>


                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    {t('common.cancelLabel')}
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    {t('common.createPayment')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Payment Modal */}
      {editingPayment && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[60]">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {t('common.editPayment')} for {selectedPersonnel?.firstName} {selectedPersonnel?.lastName}
                </h3>
                <button
                  onClick={() => setEditingPayment(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <form onSubmit={handleUpdatePayment} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('common.paymentTypeLabel')}</label>
                  <select
                    value={editPaymentData.type}
                    onChange={(e) => setEditPaymentData({...editPaymentData, type: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    {paymentTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('common.descriptionLabel')}</label>
                  <input
                    type="text"
                    value={editPaymentData.description}
                    onChange={(e) => setEditPaymentData({...editPaymentData, description: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder={t('common.descriptionPlaceholder')}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('common.amountLabel')}</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editPaymentData.amount}
                    onChange={(e) => setEditPaymentData({...editPaymentData, amount: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder={t('common.amountPlaceholder')}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('common.paymentDateLabel')}</label>
                  <input
                    type="date"
                    value={editPaymentData.paymentDate}
                    onChange={(e) => setEditPaymentData({...editPaymentData, paymentDate: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setEditingPayment(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    {t('common.cancelLabel')}
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    {t('common.updatePayment')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Payment History Modal */}
      {showHistoryModal && selectedPersonnel && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
{t('common.paymentHistoryFor')} {selectedPersonnel.firstName} {selectedPersonnel.lastName}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              {historyLoading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
              ) : (
                <div className="max-h-96 overflow-y-auto">
                  {paymentHistory.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
{t('common.noPaymentHistoryFound')}
                    </div>
                  ) : (
                    <div className="space-y-6 p-4 sm:p-6 lg:p-8">
                      {Object.entries(
                        paymentHistory.reduce((acc, payment) => {
                          const date = new Date(payment.paymentDate);
                          const monthYear = `${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear()}`;
                          
                          if (!acc[monthYear]) {
                            acc[monthYear] = [];
                          }
                          acc[monthYear].push(payment);
                          return acc;
                        }, {})
                      )
                        .sort(([a], [b]) => new Date(b.split(' ')[1], b.split(' ')[0]) - new Date(a.split(' ')[1], a.split(' ')[0]))
                        .map(([monthYear, payments]) => (
                          <div key={monthYear} className="border border-gray-200 rounded-lg">
                            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                              <h4 className="font-semibold text-gray-800">{monthYear}</h4>
                              <p className="text-sm text-gray-600">
                                {payments.length} payment{payments.length !== 1 ? 's' : ''} • 
                                Total: {payments.reduce((sum, p) => sum + parseFloat(p.amount), 0).toLocaleString()} DA
                              </p>
                            </div>
                            <div className="divide-y divide-gray-200">
                              {payments
                                .sort((a, b) => new Date(b.paymentDate) - new Date(a.paymentDate))
                                .map((payment) => (
                                  <div key={payment.id} className="px-4 py-3 hover:bg-gray-50">
                                    <div className="flex justify-between items-start">
                                      <div className="flex-1">
                                        <div className="flex items-center space-x-2">
                                          <span className="font-medium text-gray-900">{payment.description}</span>
                                        </div>
                                        <div className="text-sm text-gray-600 mt-1">
                                          <span className="font-medium">{payment.paymentType}</span>
                                          {payment.paymentMethod && (
                                            <span> • {payment.paymentMethod}</span>
                                          )}
                                          {payment.referenceNumber && (
                                            <span> • {t('common.ref')}: {payment.referenceNumber}</span>
                                          )}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1 flex items-center">
                                          {new Date(payment.paymentDate).toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric'
                                          })}
                                          {(() => {
                                            const paymentDate = new Date(payment.paymentDate);
                                            const today = new Date();
                                            today.setHours(0, 0, 0, 0);
                                            paymentDate.setHours(0, 0, 0, 0);
                                            const isPastDue = paymentDate < today;
                                            
                                            return isPastDue && payment.status === 0 && (
                                              <span className="ml-2 px-1.5 py-0.5 text-xs bg-orange-100 text-orange-800 rounded">
{t('common.pastDue')}
                                              </span>
                                            );
                                          })()}
                                        </div>
                                      </div>
                                      <div className="text-right">
                                        <div className="text-lg font-semibold text-gray-900">
                                          {parseFloat(payment.amount).toLocaleString()} DA
                                        </div>
                                        {payment.notes && (
                                          <div className="text-xs text-gray-500 mt-1 max-w-xs truncate">
                                            {payment.notes}
                                          </div>
                                        )}
                                        {/* Edit and Delete buttons - only for SuperAdmin */}
                                        {(user?.role === 4 || user?.role === 'SuperAdmin') && (
                                          <div className="flex space-x-2 mt-2">
                                            <button
                                              onClick={() => handleEditPayment(payment)}
                                              className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                                            >
                                              <Edit className="w-3 h-3 inline mr-1" />
                                              {t('common.edit')}
                                            </button>
                                            <button
                                              onClick={() => handleDeletePayment(payment.id)}
                                              className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                                            >
                                              <Trash2 className="w-3 h-3 inline mr-1" />
                                              {t('common.delete')}
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                  
                  {/* Pagination Controls */}
                  {paymentHistory.length > 0 && historyPagination.totalPages > 1 && (
                    <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4">
                      <div className="flex items-center text-sm text-gray-700">
                        <span>
{t('common.showing')} {((historyPagination.currentPage - 1) * historyPagination.itemsPerPage) + 1} {t('common.to')}{' '}
                          {Math.min(historyPagination.currentPage * historyPagination.itemsPerPage, historyPagination.totalItems)} {t('common.of')}{' '}
                          {historyPagination.totalItems} {t('common.results')}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleViewHistory(selectedPersonnel, historyPagination.currentPage - 1)}
                          disabled={historyPagination.currentPage <= 1}
                          className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
{t('common.previous')}
                        </button>
                        
                        {/* Page numbers */}
                        <div className="flex space-x-1">
                          {Array.from({ length: Math.min(5, historyPagination.totalPages) }, (_, i) => {
                            const startPage = Math.max(1, historyPagination.currentPage - 2);
                            const pageNum = startPage + i;
                            if (pageNum > historyPagination.totalPages) return null;
                            
                            return (
                              <button
                                key={pageNum}
                                onClick={() => handleViewHistory(selectedPersonnel, pageNum)}
                                className={`px-3 py-1 text-sm border rounded-md ${
                                  pageNum === historyPagination.currentPage
                                    ? 'bg-blue-500 text-white border-blue-500'
                                    : 'border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                        </div>
                        
                        <button
                          onClick={() => handleViewHistory(selectedPersonnel, historyPagination.currentPage + 1)}
                          disabled={historyPagination.currentPage >= historyPagination.totalPages}
                          className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
{t('common.next')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
            </div>

            {/* Pagination - Sticky at bottom */}
            <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
                <div className="text-xs sm:text-sm text-gray-700 font-medium">
                  {t('common.showing')} <span className="font-bold text-blue-600">{startIndex + 1}</span> {t('common.to')} <span className="font-bold text-blue-600">{Math.min(endIndex, filteredPersonnel.length)}</span> {t('common.of')} <span className="font-bold text-blue-600">{filteredPersonnel.length}</span> {t('common.results')}
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1 || totalPages <= 1}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] min-w-[44px] touch-manipulation"
                  >
                    {t('common.previous')}
                  </button>
                  
                  <div className="flex items-center space-x-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                      if (pageNum > totalPages) return null;
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 min-h-[44px] min-w-[44px] touch-manipulation ${
                            pageNum === currentPage
                              ? 'bg-blue-600 text-white shadow-lg'
                              : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages || totalPages <= 1}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] min-w-[44px] touch-manipulation"
                  >
                    {t('common.next')}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default PersonnelPayments;

























